import React, { useState, useEffect, useRef } from "react";
import {
  collection, getDocs, addDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebase-config";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO from "../components/SEO";
import {
  FaArrowRight, FaBriefcase, FaCheck, FaClock, FaFileContract,
  FaGlobeEurope, FaGraduationCap, FaHeart, FaLock, FaVideo,
} from "react-icons/fa";
import heroPortrait from "../assets/images/hero.jpg";
import TimeSlotsEditor from "../components/TimeSlotsEditor";
import { emptyTime, isSlotFilled, isSlotWideEnough, isSlotBlocked, buildProposedTimes, MIN_SESSION_MINUTES } from "../utils/guidanceTimes";
import "../Styles/GuidancePage.css";

function fmtDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
}

const TOPICS = [
  "Career guidance / getting started in law",
  "Understanding contracts as a freelancer",
  "LLM / postgrad study advice",
  "Moving to the UK for work / study",
  "Contract management as a career",
  "Starting out as a consultant",
  "Something else",
];

const MIN_TIMES = 2;
const MAX_TIMES = 3;

function genToken() {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
}

export default function GuidancePage() {
  const [blocked, setBlocked] = useState([]);
  const [times, setTimes] = useState([emptyTime(), emptyTime()]);
  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const loadTimeRef = useRef(Date.now());

  useEffect(() => {
    getDocs(collection(db, "guidance_blocked"))
      .then(snap => setBlocked(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Your name is required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "A valid email is required";
    if (!form.topic) e.topic = "Please select a topic";

    const filled = times.filter(isSlotFilled);
    if (filled.length < MIN_TIMES) {
      e.times = `Please suggest at least ${MIN_TIMES} possible times`;
    } else if (filled.some(t => !isSlotWideEnough(t))) {
      e.times = `Each range needs to be at least ${MIN_SESSION_MINUTES} minutes so a full session fits inside it.`;
    } else {
      const blockedHit = filled.find(t => isSlotBlocked(t, blocked));
      if (blockedHit) e.times = "One of your times falls when Pooja's marked herself unavailable — please pick another.";
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Bot check 1: honeypot field filled
    if (honeypot) return;

    // Bot check 2: submitted too fast (< 3 seconds since page load)
    if (Date.now() - loadTimeRef.current < 3000) return;

    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);
    try {
      const proposedTimes = buildProposedTimes(times, MAX_TIMES);
      await addDoc(collection(db, "guidance_requests"), {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        topic: form.topic,
        message: form.message.trim(),
        proposedTimes,
        status: "pending",
        round: 1,
        token: genToken(),
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch {
      setErrors({ submit: "Something went wrong — please try again." });
    }
    setSubmitting(false);
  };

  return (
    <>
      <SEO
        title="Free Guidance Sessions | Pooja Biswal — The PP Studio"
        description="Apply for a free 30-minute guidance session with Pooja Biswal — contract specialist, LLM (Distinction) Queen Mary. Open to students and early-career professionals."
        url="https://pp-studio-website.web.app/guidance"
      />
      <Navbar />

      <main className="gp-main">
        {/* Hero */}
        <section className="gp-hero">
          <div className="gp-hero-inner">
            <div className="gp-hero-copy">
              <h1 className="gp-title">
                A little clarity can<br />
                <em>change your direction.</em>
              </h1>
              <p className="gp-subtitle">
                Bring the career question you keep circling back to. We'll spend 30 thoughtful
                minutes untangling it together — honestly, practically, and completely free.
              </p>
              <div className="gp-hero-actions">
                <a className="gp-primary-link" href="#apply">Request your session <FaArrowRight /></a>
                <span className="gp-no-pitch"><FaHeart /> No pitch. Just perspective.</span>
              </div>
              <div className="gp-hero-details">
                <span><FaClock /> 30 minutes</span>
                <span><FaVideo /> One-to-one video call</span>
                <span><FaGlobeEurope /> UK time</span>
              </div>
            </div>

            <div className="gp-portrait-wrap">
              <div className="gp-portrait-frame">
                <img src={heroPortrait} alt="Pooja Biswal" />
              </div>
              <div className="gp-hand-note">
                <span>From me to you</span>
                <strong>Come as you are.</strong>
                <p>You don't need to have it all figured out.</p>
              </div>
              <span className="gp-scribble" aria-hidden="true">↝</span>
            </div>
          </div>
          <div className="gp-hero-word" aria-hidden="true">CLARITY</div>
        </section>

        <section className="gp-promise" aria-label="How it works">
          <div className="gp-promise-inner">
            <p><span>01</span><strong>You share</strong> what you want to unpack</p>
            <i />
            <p><span>02</span><strong>We talk</strong> openly for 30 minutes</p>
            <i />
            <p><span>03</span><strong>You leave</strong> with a clearer next step</p>
          </div>
        </section>

        {/* Form */}
        <section className="gp-booking" id="apply">
          <div className="gp-booking-inner">

            {submitted ? (
              <div className="gp-success">
                <span className="gp-success-icon">🌿</span>
                <h2>Request sent!</h2>
                <p>
                  Thank you, <strong>{form.name}</strong>. Pooja will check the times you suggested
                  and email you at <strong>{form.email}</strong> — either to confirm one of them,
                  or to ask you to suggest a couple more.
                </p>
                <p className="gp-success-note">
                  In the meantime, feel free to explore the{" "}
                  <a href="/contract-tips">Contract Tips</a> or{" "}
                  <a href="/blog">the blog</a>.
                </p>
              </div>
            ) : (
              <div className="gp-booking-layout">
                <aside className="gp-booking-intro">
                  <span className="gp-eyebrow">Your turn</span>
                  <h2>Let's make space for your question.</h2>
                  <p>Tell me enough to help me arrive prepared. You don't need a polished story or a perfect plan.</p>
                  <div className="gp-booking-checks">
                    <span><FaCheck /> Personal and one-to-one</span>
                    <span><FaCheck /> Friendly, practical guidance</span>
                    <span><FaCheck /> No fee, catch or follow-up pitch</span>
                  </div>
                  <blockquote>“Sometimes you don't need another answer. You need the right conversation.”</blockquote>
                </aside>

                <div className="gp-form-section">
                <div className="gp-form-heading">
                  <span>Session request</span>
                  <strong>Usually takes 2 minutes</strong>
                </div>

                <form className="gp-form" onSubmit={handleSubmit} noValidate autoComplete="off">
                  {/* Honeypot — hidden from humans, bots fill it */}
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={e => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
                    autoComplete="off"
                  />

                  <div className="gp-form-row">
                    <label className="gp-label">
                      <span>Your name <em>*</em></span>
                      <input
                        type="text"
                        placeholder="First and last name"
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        className={errors.name ? "error" : ""}
                      />
                      {errors.name && <span className="gp-field-err">{errors.name}</span>}
                    </label>

                    <label className="gp-label">
                      <span>Email address <em>*</em></span>
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        className={errors.email ? "error" : ""}
                      />
                      {errors.email && <span className="gp-field-err">{errors.email}</span>}
                    </label>
                  </div>

                  <label className="gp-label">
                    <span>What would you like to discuss? <em>*</em></span>
                    <select
                      value={form.topic}
                      onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                      className={errors.topic ? "error" : ""}
                    >
                      <option value="">Select a topic…</option>
                      {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {errors.topic && <span className="gp-field-err">{errors.topic}</span>}
                  </label>

                  <label className="gp-label">
                    <span>A little more context <em>(optional)</em></span>
                    <textarea
                      placeholder="Share anything that would help me prepare — where you are in your career, a specific challenge, or what outcome you're hoping for."
                      value={form.message}
                      onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      rows={4}
                      maxLength={800}
                    />
                  </label>

                  <div className="gp-times-block">
                    <span className="gp-times-label">Suggest {MIN_TIMES}–{MAX_TIMES} possible times <em>*</em></span>
                    <p className="gp-section-sub" style={{ margin: "0 0 0.8rem" }}>All times are UK time (BST/GMT).</p>

                    <TimeSlotsEditor times={times} setTimes={setTimes} minTimes={MIN_TIMES} maxTimes={MAX_TIMES} />

                    {errors.times && <p className="gp-error" style={{ marginTop: "0.6rem" }}>{errors.times}</p>}

                    {blocked.length > 0 && (
                      <p className="gp-blocked-note">
                        🚫 Pooja isn't available on: {blocked.map(b => `${fmtDate(b.date)}${b.time ? ` (${b.time})` : ""}`).join(", ")}
                      </p>
                    )}
                  </div>

                  {errors.submit && <p className="gp-error">{errors.submit}</p>}

                  <div className="gp-reflection-notice">
                    <strong>A small part of the exchange</strong>
                    <p>
                      After the session, you’ll receive a short, optional reflection form. It helps me understand what was useful and improve future mentoring conversations. Your response will remain private unless you separately give permission to share it — with your name or anonymously.
                    </p>
                  </div>

                  <button type="submit" className="gp-submit-btn" disabled={submitting}>
                    {submitting ? "Sending request…" : <>Request my session <FaArrowRight /></>}
                  </button>

                  <p className="gp-privacy-note">
                    <FaLock /> Your details are only used to arrange this session. No marketing, ever.
                  </p>
                </form>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* About this offering */}
        <section className="gp-about">
          <div className="gp-about-inner">
            <span className="gp-eyebrow">This space is for you if…</span>
            <h2>You’re at a crossroads — big or small.</h2>
            <p className="gp-about-lead">You don't need to fit neatly into a box. These are simply some of the conversations I can help with.</p>
            <div className="gp-about-grid">
              {[
                { icon: <FaGraduationCap />, number: "01", title: "Finding your next step", desc: "Study, an LLM, law, business — or simply working out where your strengths could take you." },
                { icon: <FaGlobeEurope />, number: "02", title: "Starting over in the UK", desc: "Studying, settling in, and navigating a new professional landscape from someone who's done it." },
                { icon: <FaBriefcase />, number: "03", title: "Building an early career", desc: "Your first role, going freelance, or making sense of a path that doesn't feel linear yet." },
                { icon: <FaFileContract />, number: "04", title: "Getting into contracts", desc: "Understanding the field, finding your niche, and deciding whether contract work is right for you." },
              ].map(card => (
                <div key={card.title} className="gp-about-card">
                  <div className="gp-card-top"><span className="gp-about-icon">{card.icon}</span><small>{card.number}</small></div>
                  <strong>{card.title}</strong>
                  <p>{card.desc}</p>
                </div>
              ))}
            </div>
            <p className="gp-closing-note">Not sure your question belongs here? <a href="#apply">Tell me anyway <FaArrowRight /></a></p>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
